/**
 * Qué se está leyendo en voz alta, ahora mismo, en toda la app.
 *
 * El audio es un singleton (`lib/audio.ts` tiene un solo `currentAudio` y una
 * sola cola de speechSynthesis): suena una cosa por vez. Entonces "qué suena"
 * también es un solo dato, y vive acá en vez de estar replicado como estado
 * local en cada vista — que es como estaba, reinventado tres veces con nombres
 * distintos (`highlight` en Sílabas y Calculadora, `readingWord` en Arrastrar).
 *
 * La identidad es una `key` que elige quien lee: la palabra, `letra:A:sonido`,
 * lo que le sirva al componente para saber qué resaltar. Ver `useSay()`.
 *
 * Para el karaoke de los cuentos hace falta un dato más fino —en qué palabra
 * del texto va— y vale el mismo razonamiento: suena una lectura por vez, así
 * que también es uno solo. Quien resalta mira las dos cosas juntas
 * (`speakingKey === miKey && speakingWord === i`), porque puede haber varias
 * páginas montadas y solo una está sonando.
 */

import { stopAll } from './audio';

type Listener = () => void;

let speakingKey: string | null = null;
/** Índice de la palabra que suena dentro de la lectura actual, para el karaoke
 *  de los cuentos. null en todo lo que se lee de una sola pieza. */
let speakingWord: number | null = null;
/** Hay un `say`/`sayEach` en curso, de principio a fin — a diferencia de
 *  `speakingKey`, que puede quedar en `null` durante toda una secuencia sin
 *  key (una explicación de error de Cazador de Letras no resalta nada, pero
 *  sigue sonando). Cubre también las pausas entre pasos de una secuencia
 *  (`gap`), que no tienen audio sonando pero tampoco terminaron: `lib/
 *  gameLock.ts` lo usa para no destrabar el juego a mitad de una frase. */
let busy = false;
const listeners = new Set<Listener>();

/** Sesión de lectura. Cambiarla invalida los callbacks de la lectura anterior:
 *  un `onEnd` que llega tarde ve que ya no es la vigente y no hace nada. */
let session = 0;

export function getSpeakingKey(): string | null {
  return speakingKey;
}

export function setSpeakingKey(key: string | null): void {
  if (speakingKey === key) return;
  speakingKey = key;
  // Arranca otra lectura: la palabra de la anterior ya no significa nada.
  speakingWord = null;
  listeners.forEach((l) => l());
}

export function getSpeakingWord(): number | null {
  return speakingWord;
}

export function setSpeakingWord(index: number | null): void {
  if (speakingWord === index) return;
  speakingWord = index;
  listeners.forEach((l) => l());
}

export function getSpeakingBusy(): boolean {
  return busy;
}

export function setSpeakingBusy(v: boolean): void {
  if (busy === v) return;
  busy = v;
  listeners.forEach((l) => l());
}

export function subscribeSpeaking(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function startSpeakingSession(): number {
  return ++session;
}

export function isSpeakingSession(s: number): boolean {
  return session === s;
}

/**
 * Corta TODO lo que esté sonando —grabación, TTS, video, música— y limpia
 * el estado de lectura. La usa la navegación (`useNav.ts`): salir de una
 * sección (atrás, home, entrar a otra) siempre corta el sonido, sea cual sea
 * el camino por el que estaba sonando (`useSay()`, un `<audio>`/`<video>`
 * propio con `claimPlayback`, el "¡Muy bien!" de `celebrate()`). Sin esto,
 * un juego que no se acuerda de cortar al desmontarse deja el sonido de la
 * pantalla anterior sonando encima de la nueva.
 */
export function stopAllSpeaking(): void {
  startSpeakingSession();
  setSpeakingKey(null);
  setSpeakingWord(null);
  setSpeakingBusy(false);
  stopAll();
}
